/**
 * 前后端共享的领域类型 —— 唯一事实来源。
 *
 * 约定：
 * - 时间一律是 ISO-8601 UTC 字符串（`nowIso()` 产生），前端负责本地化展示。
 * - 传输字段一律 camelCase；D1 里的 snake_case 只在 `worker/src/db/mappers.ts` 边界出现。
 * - API 报文统一是 `ApiResult<T>`。
 */

/* ============================================================ 通用报文 */

/** 字段级校验错误，`code = 'validation_failed'` 时挂在 `details` 上。 */
export interface FieldError {
  field: string;
  message: string;
}

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiError };

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/* ============================================================ 角色与权限 */

export const PERMISSIONS = [
  'idea.create',
  'idea.view',
  'idea.edit.own',
  'idea.edit.any',
  'idea.transition.own',
  'idea.transition.review',
  'idea.assign',
  'comment.create',
  'comment.delete.own',
  'comment.delete.any',
  'ai.run',
  'ai.view',
  'user.view',
  'user.manage',
  'settings.manage',
  'audit.view',
  'usage.view',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const ROLES = ['member', 'reviewer', 'admin'] as const;

export type Role = (typeof ROLES)[number];

export interface PermissionCatalog {
  permissions: { permission: Permission; label: string }[];
  roles: { role: Role; label: string; description: string; permissions: Permission[] }[];
}

/* ============================================================ 用户 */

export type UserStatus = 'active' | 'suspended';

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  status: UserStatus;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LoginResponse {
  user: User;
  permissions: Permission[];
  mustChangePassword: boolean;
  /** 会话过期时间（Unix 秒），前端只用它做提示，不做鉴权。 */
  expiresAt: number;
}

/* ============================================================ 创意 */

export const IDEA_STATUSES = ['draft', 'submitted', 'validating', 'validated', 'incubating', 'shipped', 'archived'] as const;

export type IdeaStatus = (typeof IDEA_STATUSES)[number];

export const AI_RUN_STATUSES = ['queued', 'running', 'succeeded', 'failed'] as const;

export type AiRunStatus = (typeof AI_RUN_STATUSES)[number];

/** 创意上的 AI 状态，与 run 表区分：这里只有最近一次的状态。 */
export const AI_STATES = ['idle', 'queued', 'running', 'succeeded', 'failed'] as const;

export type AiState = (typeof AI_STATES)[number];

export type AiVerdict = 'go' | 'needs_more_info' | 'no_go';

export type AiConfidence = 'high' | 'medium' | 'low';

export const ARTIFACT_KINDS = ['prd', 'api', 'data_model', 'code', 'experiment'] as const;

export type ArtifactKind = (typeof ARTIFACT_KINDS)[number];

/** 五维评分中的一维。 */
export interface AiDimension {
  key: string;
  label: string;
  /** 0–100。 */
  score: number;
  note: string;
}

export type AiRiskSeverity = 'high' | 'medium' | 'low';

export interface AiRisk {
  title: string;
  severity: AiRiskSeverity;
  detail: string;
  mitigation: string;
}

export interface AiAsset {
  name: string;
  kind: string;
  note: string;
}

export type AiEffort = 'S' | 'M' | 'L';

export interface AiStep {
  order: number;
  title: string;
  detail: string;
  effort: AiEffort;
}

export interface AiArtifact {
  kind: ArtifactKind;
  title: string;
  /** 代码片段用得上；其它产物为 null。 */
  language: string | null;
  content: string;
}

export interface AiReport {
  id: string;
  ideaId: string;
  runId: string;
  verdict: AiVerdict;
  /** 0–100 综合分。 */
  score: number;
  confidence: AiConfidence;
  summary: string;
  dimensions: AiDimension[];
  risks: AiRisk[];
  reusableAssets: AiAsset[];
  mvpSteps: AiStep[];
  artifacts: AiArtifact[];
  openQuestions: string[];
  model: string;
  promptVersion: string;
  createdAt: string;
}

export interface AiRun {
  id: string;
  ideaId: string;
  status: AiRunStatus;
  provider: string;
  model: string;
  promptVersion: string;
  triggeredBy: string;
  triggeredByName: string;
  error: string | null;
  latencyMs: number | null;
  reportId: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  /** 推理型模型的思考过程（截断后的摘要）；不支持或未开启时为空串。 */
  reasoningExcerpt: string;
  /** 思考过程消耗的 token。这类模型的成本大头在思考上，单独记一列才解释得清。 */
  reasoningTokens: number | null;
}

/** 与「当前访客」相关的状态，服务端按会话计算，不落库。 */
export interface IdeaViewerState {
  liked: boolean;
  followed: boolean;
  /** 我参与过的创意里有新评论或状态变更。 */
  unread: boolean;
}

export interface Idea {
  id: string;
  title: string;
  summary: string;
  body: string;
  hypothesis: string;
  audience: string;
  successMetric: string;
  tags: string[];
  status: IdeaStatus;
  authorId: string;
  authorName: string;
  assigneeId: string | null;
  assigneeName: string | null;
  dueAt: string | null;
  aiState: AiState;
  latestReportId: string | null;
  likeCount: number;
  commentCount: number;
  createdAt: string;
  updatedAt: string;
  reviewedAt: string | null;
  archivedAt: string | null;
}

export interface IdeaListItem extends Idea {
  viewer: IdeaViewerState;
}

/** 流转按钮的展示模型；`actor` 在 ideaStatus.ts 里补上（那里是状态机的唯一事实来源）。 */
export type TransitionTone = 'primary' | 'default' | 'danger';

export interface IdeaTransitionOption {
  from: IdeaStatus;
  to: IdeaStatus;
  label: string;
  description: string;
  /** 执行前必须先选定负责人（立项要求）。 */
  requiresAssignee: boolean;
  tone: TransitionTone;
}

export interface IdeaDetail extends Idea {
  viewer: IdeaViewerState;
  latestRun: AiRun | null;
  latestReport: AiReport | null;
}

/** 详情页一次取全，避免打开一个创意要发五个请求。 */
export interface IdeaDetailResponse extends IdeaDetail {
  comments: IdeaComment[];
  events: IdeaEvent[];
  runs: AiRun[];
  reports: AiReport[];
  /** 当前登录账号可执行的流转（不含系统自动流转）。 */
  transitions: IdeaTransitionOption[];
  /** 当前账号是否还能编辑正文。 */
  canEdit: boolean;
  /** 正文在最近一次研判之后被改过，结论可能已过时。 */
  reportStale: boolean;
}

export interface IdeaComment {
  id: string;
  ideaId: string;
  authorId: string;
  authorName: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export const IDEA_EVENT_TYPES = ['created', 'status', 'assignee', 'comment', 'ai_run', 'edit'] as const;

export type IdeaEventType = (typeof IDEA_EVENT_TYPES)[number];

/** 创意动态流 —— 用它替代复杂的工单历史与审批记录。 */
export interface IdeaEvent {
  id: string;
  ideaId: string;
  actorId: string | null;
  actorName: string;
  type: IdeaEventType;
  fromValue: string | null;
  toValue: string | null;
  /** 面向人的一句话描述，服务端渲染好，前端直接显示。 */
  summary: string;
  at: string;
}

export type ReactionKind = 'like' | 'follow';

/* ============================================================ 平台设置 */

export interface PlatformSettings {
  /** 是否允许发起 AI 研判。 */
  aiEnabled: boolean;
  /** 服务端计算：AI_BASE_URL / AI_API_KEY 是否已注入。只读，不接受写入。 */
  aiConfigured: boolean;
  /** 每人每日可发起的研判次数，0 表示不限。按 UTC 日切。 */
  aiDailyLimit: number;
  /** 展示用的模型名，实际调用以 Worker 的 AI_MODEL 为准。 */
  aiModel: string;
  /** 可选分类标签，用于看板筛选与提交时的快捷选择。 */
  tags: string[];
  /** 团队能力清单：现有系统、技术栈、可复用资产。作为研判上下文注入提示词。 */
  capabilities: string;
}

export type PlatformSettingsPatch = Partial<Omit<PlatformSettings, 'aiConfigured'>>;

/* ============================================================ 统计 */

export interface StatsOverview {
  total: number;
  byStatus: { status: IdeaStatus; count: number }[];
  byTag: { tag: string; count: number }[];
  ai: {
    runs: number;
    succeeded: number;
    failed: number;
    goCount: number;
    needsMoreInfoCount: number;
    noGoCount: number;
    avgScore: number | null;
    /** 取得 go 结论的比例（0–1），无有效报告时为 null。 */
    passRate: number | null;
  };
  people: {
    authors: number;
    assignees: number;
  };
  recentEvents: IdeaEvent[];
  /** 当前访客自己的 AI 用量。只含本人数字，不暴露他人。 */
  myUsage: UsageBucket;
}

/* ============================================================ 资源用量 */

/**
 * 一段统计口径下的 AI 用量。
 *
 * 为什么 token 与请求次数要并列：两者互不可推导。
 * 同样一万 token，可能是 1 次完整研判，也可能是 3 次短请求 ——
 * 只给 token 说不清「调了几次」，只给次数说不清「烧了多少」。
 *
 * 另一个必须显式记录的量是 `meteredRequests`：网络错误、超时、401 这类失败
 * 发生在收到响应之前，响应里根本没有 `usage` 块，这些 run 的 token 列是 NULL。
 * 它们只能按次数计 —— 如果只用 SUM 算 token，这部分消耗会**静默消失**。
 */
export interface UsageBucket {
  /** 发起研判的总次数（含未拿到 token 的失败）。 */
  requests: number;
  /** 其中拿到了 `usage` 计量的次数。`requests - meteredRequests` 就是未计量部分。 */
  meteredRequests: number;
  promptTokens: number;
  completionTokens: number;
  /** 输出 token 中属于思考过程的部分（已包含在 completionTokens 内，不重复累加）。 */
  reasoningTokens: number;
  /** 计费总量：promptTokens + completionTokens。 */
  totalTokens: number;
  succeeded: number;
  failed: number;
}

export interface MemberUsage extends UsageBucket {
  userId: string;
  name: string;
  email: string;
  role: Role;
  status: UserStatus;
  /** 该成员最近一次发起研判的时间；从未发起过为 null。 */
  lastRunAt: string | null;
}

export interface DailyUsage extends UsageBucket {
  /** UTC 日期，`YYYY-MM-DD`。 */
  day: string;
}

export interface UsageRange {
  /** ISO 起始时间（含）；null 表示不设下限。 */
  from: string | null;
  /** ISO 结束时间（不含）；null 表示不设上限。 */
  to: string | null;
}

export interface UsageOverview {
  range: UsageRange;
  /** 整个平台在这段范围内的合计，与 `members` 的求和必须相等。 */
  total: UsageBucket;
  members: MemberUsage[];
  /** 按 UTC 日期升序，供趋势图使用。 */
  byDay: DailyUsage[];
  generatedAt: string;
}

/* ============================================================ 审计 */

export const AUDIT_TARGET_TYPES = ['auth', 'user', 'idea', 'settings', 'system'] as const;

export type AuditTargetType = (typeof AUDIT_TARGET_TYPES)[number];

export type AuditResult = 'success' | 'denied' | 'failure';

export interface AuditLog {
  id: string;
  at: string;
  actorUserId: string | null;
  actorEmail: string;
  action: string;
  targetType: AuditTargetType;
  targetId: string | null;
  detail: Record<string, unknown> | null;
  result: AuditResult;
  ip: string | null;
}
